import React, { useEffect, useState } from "react"
import DateRangePresets from "../DateRangePresets"

export const fmt = (n) => new Intl.NumberFormat().format(n ?? 0)

export const qs = (obj) =>
  Object.entries(obj)
    .filter(([, v]) => v !== "" && v != null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&")

const inputCls = "rounded-lg border border-gray-300 dark:border-gray-700 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:ring-brand-500"
const btn = "inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-sm font-semibold"

export function StatCard({ label, value }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
      <dt className="text-sm text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">{fmt(value)}</dd>
    </div>
  )
}

export function PivotTable({ title, rows, copy }) {
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0) || 1
  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">—</p>
      ) : (
        <table className="mt-3 w-full text-sm">
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td className="py-1.5 pr-3">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 rounded bg-brand-50 dark:bg-brand-950" style={{ width: `${(r.count / max) * 100}%` }} />
                    <span className="relative truncate text-gray-700 dark:text-gray-300">{r.label}</span>
                  </div>
                </td>
                <td className="w-16 py-1.5 text-right font-medium text-gray-900 dark:text-gray-100">{fmt(r.count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

export function Trend({ title, points }) {
  const max = points.reduce((m, p) => Math.max(m, p.count), 0) || 1
  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
      <div className="mt-4 flex h-32 items-end gap-0.5">
        {points.map((p) => (
          <div key={p.date} title={`${p.date}: ${p.count}`} className="flex h-full flex-1 flex-col items-center justify-end">
            <span className="mb-0.5 text-[10px] font-medium tabular-nums text-gray-500 dark:text-gray-400">{p.count > 0 ? fmt(p.count) : ""}</span>
            <div
              className="w-full rounded-t bg-brand-500/80 hover:bg-brand-600"
              style={{ height: `${Math.max((p.count / max) * 100, 2)}%` }}
            />
          </div>
        ))}
      </div>
    </section>
  )
}

const DONUT_COLORS = ["#6366f1", "#06b6d4", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#64748b"]

// Donut chart for a single dimension's top values. Shares the pivot row shape
// ({ label, count }). Slices beyond the palette fold into a trailing "Other".
export function Donut({ title, rows }) {
  const slices = rows.slice(0, DONUT_COLORS.length)
  const total = slices.reduce((s, r) => s + r.count, 0) || 1
  let acc = 0
  const segs = slices.map((r, i) => {
    const start = acc / total
    acc += r.count
    const end = acc / total
    return { ...r, color: DONUT_COLORS[i % DONUT_COLORS.length], pct: Math.round((r.count / total) * 100), start, end }
  })
  const arc = (f) => {
    const a = 2 * Math.PI * f - Math.PI / 2
    return [16 + 16 * Math.cos(a), 16 + 16 * Math.sin(a)]
  }
  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
      {segs.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">—</p>
      ) : (
        <div className="mt-4 flex items-center gap-5">
          <svg viewBox="0 0 32 32" className="h-32 w-32 shrink-0 -rotate-0">
            {segs.map((s) => {
              const [x1, y1] = arc(s.start)
              const [x2, y2] = arc(s.end)
              const large = s.end - s.start > 0.5 ? 1 : 0
              return <path key={s.label} d={`M16 16 L${x1} ${y1} A16 16 0 ${large} 1 ${x2} ${y2} Z`} fill={s.color} />
            })}
            <circle cx="16" cy="16" r="8" className="fill-white dark:fill-gray-900" />
          </svg>
          <ul className="min-w-0 flex-1 space-y-1.5 text-sm">
            {segs.map((s) => (
              <li key={s.label} className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: s.color }} />
                <span className="truncate text-gray-700 dark:text-gray-300">{s.label}</span>
                <span className="ml-auto shrink-0 font-medium tabular-nums text-gray-900 dark:text-gray-100">{fmt(s.count)}</span>
                <span className="w-10 shrink-0 text-right text-xs text-gray-400 dark:text-gray-500">{s.pct}%</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

// Funnel chips — ordered funnel events with count + conversion vs listing views
// (the Mantle top-card row). Events with no data are dimmed, not hidden.
export function FunnelChips({ funnel, copy }) {
  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex items-baseline justify-between">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{copy.title}</h2>
        <span className="text-sm text-gray-500 dark:text-gray-400">{copy.totalViews}: {fmt(funnel.views)}</span>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {funnel.events.map((e) => (
          <div key={e.event} className={`min-w-28 rounded-lg border px-3 py-2 ${e.count > 0 ? "border-gray-200 dark:border-gray-800" : "border-gray-100 dark:border-gray-800 opacity-50"}`}>
            <div className="text-xs text-gray-500 dark:text-gray-400">{e.event}</div>
            <div className="mt-0.5 flex items-baseline gap-1.5">
              <span className="text-lg font-bold text-gray-900 dark:text-gray-100">{fmt(e.count)}</span>
              {e.pct != null && <span className="text-xs text-gray-400 dark:text-gray-500">{e.pct}%</span>}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

// Per-referrer conversion pivot: views → clicks → installed(%) → subscribed(%).
export function ConversionPivot({ rows, copy }) {
  const th = "px-3 py-2 text-left font-semibold text-gray-500 dark:text-gray-400"
  const td = "px-3 py-2 text-gray-700 dark:text-gray-300"
  const num = "px-3 py-2 text-right text-gray-900 dark:text-gray-100"
  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{copy.title}</h2>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">—</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="border-b border-gray-200 dark:border-gray-800">
              <tr>
                <th className={th}>{copy.referrer}</th>
                <th className={`${th} text-right`}>{copy.views}</th>
                <th className={`${th} text-right`}>{copy.clicks}</th>
                <th className={`${th} text-right`}>{copy.installed}</th>
                <th className={`${th} text-right`}>{copy.subscribed}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {rows.map((r) => (
                <tr key={r.label}>
                  <td className={td}>{r.label}</td>
                  <td className={num}>{fmt(r.cells["App Listing Page View"])}</td>
                  <td className={num}>{fmt(r.cells["Add App Button Clicked"])}</td>
                  <td className={num}>
                    {fmt(r.cells["Installed"])}
                    {r.installPct != null && <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">{r.installPct}%</span>}
                  </td>
                  <td className={num}>
                    {fmt(r.cells["Subscribed"])}
                    {r.subscribePct != null && <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">{r.subscribePct}%</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

export function Notice({ title, body, cta, href }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-8 text-center">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-gray-600 dark:text-gray-400">{body}</p>
      {cta && href && (
        <a href={href} className="mt-4 inline-flex items-center justify-center rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700">
          {cta}
        </a>
      )}
    </div>
  )
}

// Filter bar — dropdown options come from the overview pivots so they reflect
// the partner's real dimension values. `draft` is local edits; Apply lifts them.
export function FilterBar({ draft, setDraft, options, onApply, onClear, onPreset, copy }) {
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }))
  const select = (key, label, opts) => (
    <label className="flex flex-col gap-1 text-xs text-gray-500 dark:text-gray-400">
      {label}
      <select value={draft[key] || ""} onChange={set(key)} className={inputCls}>
        <option value="">{copy.all}</option>
        {opts.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </label>
  )
  return (
    <div className="space-y-3 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
      {onPreset && <DateRangePresets onSelect={onPreset} labels={copy.presets} />}
      <div className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1 text-xs text-gray-500 dark:text-gray-400">
        {copy.from}
        <input type="date" value={draft.from || ""} onChange={set("from")} className={inputCls} />
      </label>
      <label className="flex flex-col gap-1 text-xs text-gray-500 dark:text-gray-400">
        {copy.to}
        <input type="date" value={draft.to || ""} onChange={set("to")} className={inputCls} />
      </label>
      {select("app_store_slug", copy.app, options.apps)}
      {select("country", copy.country, options.countries)}
      {select("referrer_site", copy.referrer, options.referrers)}
      <div className="flex gap-2">
        <button type="button" onClick={onApply} className={`${btn} bg-brand-600 text-white hover:bg-brand-700`}>{copy.apply}</button>
        <button type="button" onClick={onClear} className={`${btn} border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800`}>{copy.clear}</button>
      </div>
      </div>
    </div>
  )
}

// Paginated raw-row table. `rowsPath` already carries format + a trailing `?`.
export function RawTable({ rowsPath, filters, copy }) {
  const [data, setData] = useState(null)
  const [page, setPage] = useState(1)

  useEffect(() => { setPage(1) }, [filters])

  useEffect(() => {
    let active = true
    const query = qs({ ...filters, view: "rows", page })
    fetch(`${rowsPath}&${query}`, { headers: { Accept: "application/json" } })
      .then((r) => r.json())
      .then((d) => active && setData(d))
    return () => { active = false }
  }, [rowsPath, filters, page])

  if (!data) return <p className="text-sm text-gray-500 dark:text-gray-400">…</p>
  if (data.total === 0) return <p className="rounded-xl border border-gray-200 dark:border-gray-800 p-6 text-sm text-gray-500 dark:text-gray-400">{copy.empty}</p>

  const th = "px-3 py-2 text-left font-semibold text-gray-500 dark:text-gray-400"
  const td = "px-3 py-2 text-gray-700 dark:text-gray-300"
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
        <table className="min-w-full text-sm">
          <thead className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <tr>
              <th className={th}>{copy.date}</th>
              <th className={th}>{copy.app}</th>
              <th className={th}>{copy.event}</th>
              <th className={th}>{copy.referrer}</th>
              <th className={th}>{copy.search}</th>
              <th className={th}>{copy.country}</th>
              <th className={`${th} text-right`}>{copy.count}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {data.rows.map((r, i) => (
              <tr key={i}>
                <td className={td}>{r.eventDate}</td>
                <td className={td}>{r.appStoreSlug}</td>
                <td className={td}>{r.eventName}</td>
                <td className={td}>{r.referrerSite}</td>
                <td className={td}>{r.searchTerm}</td>
                <td className={td}>{r.country}</td>
                <td className={`${td} text-right font-medium text-gray-900 dark:text-gray-100`}>{fmt(r.eventCount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
        <span>{copy.showing.replace("%{count}", fmt(data.total))}</span>
        <div className="flex items-center gap-3">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className={`${btn} border border-gray-200 dark:border-gray-800 disabled:opacity-40`}>{copy.prev}</button>
          <span>{copy.page.replace("%{page}", data.page).replace("%{total}", data.totalPages)}</span>
          <button type="button" disabled={page >= data.totalPages} onClick={() => setPage((p) => p + 1)} className={`${btn} border border-gray-200 dark:border-gray-800 disabled:opacity-40`}>{copy.next}</button>
        </div>
      </div>
    </div>
  )
}
